import { GDFMetarTafFlightCategory } from '../gdf/type';

export interface RadarSatelliteSettings {
    nexradMinLevel: number;
    radarSmoothing: boolean;
    nexradRainfallMinLevel: number;
    nexradEchoTopsMinLevel: number;
    satelliteMinLevel: number;
    satelliteMinTemp: number;
    radarSummaryShowSpeedLabel: boolean;
    radarSummarySpeedArrowStyle: RadarSummaryArrowStyle;
    enableRadarTransparency: boolean;
    radarTransparency: number;
    enableSatelliteTransparency: boolean;
    satelliteTransparency: number;
}

export enum RadarSummaryArrowStyle {
    speedBased,
    fixed
}

export enum WindDepiction {
    none,
    barbs,
    particles
}

export enum HeatmapStyle {
    none,
    winds,
    temperature
}

export enum BarbColorType {
    fixed,
    intensity,
    auto
}

export enum WeatherJetStreamWidth {
    extraThin,
    thin,
    medium,
    wide
}

export enum ParticleColor {
    auto,
    light,
    dark
}

export enum WindTemp {
    both,
    windOnly,
    temperatureOnly
}

export enum BarbSize {
    small,
    medium,
    large
}

export interface WindsSettings {
    windDepiction: WindDepiction;
    heatmapStyle: HeatmapStyle;
    heatmapOpacity: number;
    particleColor: ParticleColor;
    particleOpacity: number;
    barbColorType: BarbColorType;
    barbColor: string | null;
    barbSize: BarbSize;
    jetStreamWidth: WeatherJetStreamWidth;
    windTemp: WindTemp;

    probSeverePrimaryDisplay: ProbSeverePrimaryDisplay;
    probSeverePrimaryMinLevel: number;
    probSevereSecondaryDisplay: ProbSevereSecondaryDisplay;
    probSevereSecondaryMinLevel: number;
    probSevereShowArrow: boolean;
    probSevereShowTrail: boolean;
}

export type InterpolationType = 'linear' | 'nearest';

export interface RasterInterpolationSettings {
    paletteInterpolation: InterpolationType;
    rasterInterpolation: InterpolationType;
}

export enum MetarDisplayMode {
    category,
    categorySky,
    categorySkyWind,
    categorySkyWindValues,
    ceiling,
    visibility
}

export enum MinimumIcing {
    trace,
    light,
    moderate,
    heavy
}

export enum MinimumCeiling {
    unknown,
    less200,
    less500,
    less1000,
    less2000,
    less3000,
    less5000,
    less10000,
    more10000
}

export enum MinimumVisibility {
    unknown,
    lessQuarter,
    lessHalf,
    less1,
    less2,
    less3,
    less5,
    less10,
    more10
}

export enum FaaDelayMinimum {
    noDelays,
    arrDep16to45,
    arrDepMore45,
    groundDelayStop,
    closure
}

export enum RvrMinimum {
    all,
    less6000,
    less2400,
    less1200,
    less600,
    less300
}

export enum MinimumDsua {
    notScheduled,
    h12_24,
    h8_12,
    h4_8,
    h2_4,
    h1_2,
    h_1,
    active
}

export enum MinimumPirepTurbulence {
    smooth,
    light,
    moderate,
    severe,
    extreme
}

export enum MinimumPirepIcing {
    negative,
    trace,
    light,
    moderate,
    severe
}

export enum IataTurbValue {
    peak,
    mean
}

export enum IataNullReportColor {
    gray,
    white
}

export enum IataTurbulenceDisplayType {
    edr,
    light,
    medium,
    heavy
}

export enum IataTurbulencePalette {
    industry,
    iata
}

export enum MinimumTapsLevel {
    smooth,
    rideQuality,
    light,
    moderate,
    severe
}

export enum ProbSeverePrimaryDisplay {
    combined,
    wind,
    hail,
    tornado
}

export enum ProbSevereSecondaryDisplay {
    none,
    wind,
    hail,
    tornado
}

export interface WeatherAirportSettings {
    displayMode: MetarDisplayMode;
    showMetarDetailInColor: boolean;
    minimumCategory: GDFMetarTafFlightCategory;
    minimumCeiling: MinimumCeiling;
    minimumVisibility: MinimumVisibility;

    faaDelayMinimum: FaaDelayMinimum;
    rvrMinimum: RvrMinimum;

    filterGroup: string | null;
    myFleetAirports: boolean;
}

export interface WeatherDynamicSuaSettings {
    show: boolean;
    alert: boolean;
    caution: boolean;
    danger: boolean;
    militaryArea: boolean;
    prohibited: boolean;
    restricted: boolean;
    training: boolean;
    warning: boolean;
    unknown: boolean;
    filterGroup: {
        show: boolean;
        ids: string[];
    };
    minimumLevel: MinimumDsua;
}

export interface WeatherAirspaceSettings {
    showTurbulence: boolean;
    minimumTurbulence: MinimumPirepTurbulence;
    showIcing: boolean;
    minimumIcing: MinimumPirepIcing;
    showWeather: boolean;
    showWindShear: boolean;
    minAltitude: number | null;
    maxAltitude: number | null;
    reportedLast: number | null;
    aircraftSmall: boolean;
    aircraftLarge: boolean;
    aircraftHeavy: boolean;
    aircraftUnknown: boolean;
    showFca: boolean;
    showFea: boolean;
    showFcaFsm: boolean;
    showFcaAfp: boolean;
    tfrApplyTransparency: boolean;
}

export interface WeatherAirmetsSigmetsSettings {
    showTurb: boolean;
    showIcing: boolean;
    showConvective: boolean;
    showVolcanicAsh: boolean;
    showBlowingDust: boolean;
    showVisibility: boolean;
    showSurfaceWind: boolean;
    showBlowingSand: boolean;
    showOzone: boolean;
    showIfr: boolean;
    showMountainObscuration: boolean;
    showMountainWave: boolean;
    showTropicalCyclone: boolean;
    showThunderstorm: boolean;
    showLlws: boolean;
    showFreezingLevel: boolean;
}

export interface WeatherLightningSettings {
    symbol: WeatherLightningSymbol;
    color: WeatherLightningColor;
}

export interface WeatherTurbulenceIcingSettings {
    minimumIataLevel: number;
    iataTurbValue: IataTurbValue;
    iataDisplayNullReport: boolean;
    iataNullReportColor: IataNullReportColor;
    iataDisplayType: IataTurbulenceDisplayType;
    iataSeverityPalette: IataTurbulencePalette;
    iataShowWindBarbs: boolean;
    iataMaxReportTime: number;
    iataFadeOldReports: boolean;
    minimumTapsLevel: MinimumTapsLevel;
    minimumEdrLevel: number;
    minimumIcingLevel: MinimumIcing;
    icingApplyTransparency: boolean;
    turbulenceApplyTransparency: boolean;
}

export interface WeatherAnimationSettings {
    running: boolean;
    timeToShowSlideSeconds: number;
    numberOfSlides: number;
    pauseBetweenCyclesSeconds: number;
    timelineSyncMode: WeatherTimelineSyncMode;
    timelineSyncCustomProduct: number | null;
    timelineTimeRange: WeatherTimelineRange;
    timelineTimeMode: WeatherTimelineTimeMode;
}

export interface WeatherAltitudeSettings {
    value: number;
}

export interface WeatherMiscSettings {
    minimumContrailsLevel: MinimumContrailsLevel;
    enablePolygonTransparency: boolean;
    polygonTransparency: number;
    enhancedThunderStormMinLevel: number;
    minimumAshCloudConcentration: MinimumAshCloudConcentration;
}

export interface WeatherOtherSettings {
    autoArrangeLayers: boolean;
}

export interface WeatherSigWxSettings {
    labelsMinZoomLevel: number;
}

export interface WeatherSettings {
    /**
     * @deprecated
     */
    rasterInterpolation: RasterInterpolationSettings;
    animation: WeatherAnimationSettings;
    altitude: WeatherAltitudeSettings;
    winds: WindsSettings;
    radar: RadarSatelliteSettings;
    turbulenceIcing: WeatherTurbulenceIcingSettings;
    airmetsSigmets: WeatherAirmetsSigmetsSettings;
    lightning: WeatherLightningSettings;
    airport: WeatherAirportSettings;
    airspace: WeatherAirspaceSettings;
    dsua: WeatherDynamicSuaSettings;
    misc: WeatherMiscSettings;
    other: WeatherOtherSettings;
    sigWx: WeatherSigWxSettings;
}

export enum WeatherSettingsChangeSource {
    all,
    unknown,
    animation,
    animationRunning,
    altitudeValue,
    airportMetarTafDisplay,
    airportMetarTafFilter,
    airportDelays,
    airportRvr,
    airportFilterGroup,
    airportMyAirports,
    airspacePireps,
    airspaceTfrs,
    airspaceFcaFea,
    airmetsSigmets,
    lightning,
    winds,
    windsDepiction,
    windsHeatmapStyle,
    windsBarbsColor,
    windsHeatmapTransparency,
    windsTemp,
    windsBarbSize,
    dsua,
    radarSummary,
    radarSummaryStyle,
    radarSatellite,
    turbulenceIata,
    turbulenceTaps,
    turbulenceEdr,
    turbulenceTransparency,
    icing,
    icingTransparency,
    contrails,
    enhancedThunderStormMinLevel,
    autoArrange,
    polygonTransparency,
    jetStreamWidth,
    sigWx,
    probSevere,
    minimumAshCloudConcentration
}

export enum WeatherTimelineSyncMode {
    auto,
    mostFrequent,
    topMost,
    bottomMost,
    custom
}

export enum WeatherTimelineRange {
    auto,
    historyShort,
    historyMid,
    historyLong,
    forecastShort,
    forecastMid,
    forecastLong,
    full
}

export enum WeatherTimelineTimeMode {
    relative,
    absolute
}

export enum MinimumContrailsLevel {
    low,
    moderate,
    high,
    extreme
}

export enum WeatherLightningSymbol {
    dot,
    plus,
    sign,
    icon
}

export enum WeatherLightningColor {
    faded,
    discrete
}

export enum MinimumAshCloudConcentration {
    low,
    medium,
    high,
    veryHigh
}

export const rvrMinValues: Record<RvrMinimum, number> = {
    [RvrMinimum.all]: 100,
    [RvrMinimum.less6000]: 60,
    [RvrMinimum.less2400]: 24,
    [RvrMinimum.less1200]: 12,
    [RvrMinimum.less600]: 6,
    [RvrMinimum.less300]: 3
};
